import { Icon } from '@iconify/react'
import { Spin } from 'antd'
import classNames from 'classnames'
import { Button } from 'common/components/Button/Button'
import Popup from 'common/components/Popup'
import { APP_ENVIRONMENT_CONSTANTS } from 'common/constants/appEnvironmentConstants'
import { BUTTON_MODIFIER, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { useAppSelector } from 'common/hooks/redux'
import { env } from 'common/utils/env'
import { EMPTY_PROCEDURE_CODE } from 'features/Procedure/features/treatments/components/manage-procedure/utils'
import { usePrintDrawings } from 'features/Procedure/hooks/usePrintDrawings'
import { useSaveDrawing } from 'features/Procedure/hooks/useSaveDrawing'
import type { CanvasCaptureRef } from 'features/Procedure/procedure-model/canvas-capture'
import { useFetchProceduresQuery } from 'features/Procedure/state/procedure.api'
import React from 'react'
import { useParams } from 'react-router'
import styles from './print-button.module.scss'

type Props = {
  canvasCaptureRef: React.RefObject<CanvasCaptureRef>
  selectedPatientId: number | null
}

export const PrintButton = React.memo(({ canvasCaptureRef, selectedPatientId }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )
  const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
  const { handleSaveImages, handlePrintPdf, isPrinting, progress, hasProcedures } =
    usePrintDrawings({
      canvasCaptureRef,
      selectedPatientId,
    })
  const { hasElementToSave } = useSaveDrawing()
  const { data: proceduresData } = useFetchProceduresQuery(
    {
      siteId: siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid || !siteId,
    },
  )

  const hasEmptyProcedures = proceduresData?.items?.some(
    (procedure) => procedure.code === EMPTY_PROCEDURE_CODE.toString(),
  )

  const handlePrintClick = () => {
    if (hasEmptyProcedures) {
      Popup.showInfo({
        title: 'Error',
        message:
          'Cannot print with empty procedures. Please complete all procedure codes before printing.',
        mainButtonText: 'OK',
      })
      return
    }
    // handlePrintPdf({downloadLocally: process.env.VITE_ENV === APP_ENVIRONMENT_CONSTANTS.DEVELOPMENT, showOnlyCurrentProcedure: true})

    handleSaveImages({
      downloadLocally: env.ENV === APP_ENVIRONMENT_CONSTANTS.DEVELOPMENT,
      showOnlyCurrentProcedure: true,
    })
  }

  const isActionDisabled = !selectedEncounterUid || !selectedProcedure || isPrinting

  return (
    <>
      <Spin spinning={isPrinting} fullscreen size='large' tip={progress || 'Generating PDF...'} />
      <div className={styles.container}>
        <Button
          className={classNames(styles.printButton, {
            [styles.printing]: isPrinting,
            [styles.hasUnsavedChanges]: hasElementToSave,
          })}
          icon={<Icon icon='mdi:content-save' fontSize={16} />}
          onClick={handlePrintClick}
          disabled={isActionDisabled || hasElementToSave || !hasProcedures}
          loading={isPrinting}
          modifier={BUTTON_MODIFIER.PRIMARY}
          severity={BUTTON_SEVERITY.SUCCESS_FILLED}>
          Save
        </Button>
      </div>
    </>
  )
})
